/**
 * Junior Fit Configurator (Ping Prodi G)
 *
 * Adds the companion product variant to the cart with the fitting choices
 * attached as line item properties. No surcharge products: nothing in this
 * configurator carries a price uplift.
 *
 * Shaft flex and putter length are derived from player height via the
 * height_map JSON on the Junior Fit Template metaobject.
 *
 * The custom dropdown builder is ported from configurator.js so the fields
 * look identical to the main configurator. It is duplicated rather than
 * shared because configurator.js scopes itself to [data-configurator] and
 * running it on this page would bring its club/shaft/surcharge logic with it.
 *
 * Cart drawer handling mirrors configurator.js: set a sessionStorage flag,
 * reload, then open the Alpine mini cart on next init.
 */

(function () {
  'use strict';

  var CURRENCY = '\u00A3';

  var root = document.querySelector('[data-junior-fit]');
  if (!root) return;

  /* ================================================================== */
  /* Helpers                                                             */
  /* ================================================================== */

  function money(pence) {
    return (pence / 100).toFixed(2);
  }

  function selectValue(selector) {
    var el = root.querySelector(selector);
    return el && el.value ? el.value : '';
  }

  /* Returns null for the hidden placeholder option, not just for no select. */
  function selectedOption(selector) {
    var el = root.querySelector(selector);
    if (!el || el.selectedIndex < 0) return null;
    var opt = el.options[el.selectedIndex];
    if (!opt || !opt.value) return null;
    return opt;
  }

  function readHeightMap() {
    try {
      return JSON.parse(root.dataset.heightMap || '[]');
    } catch (e) {
      console.error('[junior-fit] height_map is not valid JSON', e);
      return [];
    }
  }

  var HEIGHT_MAP = readHeightMap();

  function currentHeightRow() {
    var value = selectValue('[data-junior-height]');
    if (!value) return null;
    for (var i = 0; i < HEIGHT_MAP.length; i++) {
      if (String(HEIGHT_MAP[i].height) === String(value)) return HEIGHT_MAP[i];
    }
    return null;
  }

  /* Derived fields stay visible at all times. Until a height is chosen they
     show placeholder text greyed out, rather than being hidden, so the
     customer can see the field exists and is not something they choose. */
  function setDerived(wrapSelector, textSelector, value) {
    var wrap = root.querySelector(wrapSelector);
    if (!wrap) return;
    var textEl = root.querySelector(textSelector);
    if (value) {
      if (textEl) textEl.textContent = value;
      wrap.classList.remove('is-pending');
    } else {
      if (textEl) textEl.textContent = 'Set by player height';
      wrap.classList.add('is-pending');
    }
  }

  function showError(message) {
    var el = root.querySelector('[data-junior-error]');
    if (!el) return;
    if (message) {
      el.textContent = message;
      el.classList.remove('is-hidden');
    } else {
      el.textContent = '';
      el.classList.add('is-hidden');
    }
  }

  /* ================================================================== */
  /* Custom dropdown builder (ported from configurator.js)               */
  /* ================================================================== */

  var arrowSVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" ' +
    'stroke="#999" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>';

  function buildSingleCustomDropdown(select) {
    var field = select.closest('.config-field');
    if (!field) return;

    var labelEl = field.querySelector('.config-label');
    var labelText = '';
    if (labelEl) {
      labelText = labelEl.textContent.trim();
      field.dataset.configLabel = labelText;
      labelEl.remove();
    } else {
      labelText = field.dataset.configLabel || '';
    }

    var dropdown = document.createElement('div');
    dropdown.className = 'custom-dropdown';
    if (select.disabled) dropdown.classList.add('is-disabled');

    var trigger = document.createElement('div');
    trigger.className = 'custom-dropdown__trigger';

    var label = document.createElement('span');
    label.className = 'custom-dropdown__label';
    label.textContent = labelText;

    var value = document.createElement('span');
    value.className = 'custom-dropdown__value';

    var arrow = document.createElement('span');
    arrow.className = 'custom-dropdown__arrow';
    arrow.innerHTML = arrowSVG;

    trigger.appendChild(label);
    trigger.appendChild(value);
    trigger.appendChild(arrow);

    var optionsPanel = document.createElement('div');
    optionsPanel.className = 'custom-dropdown__options';

    Array.prototype.forEach.call(select.options, function (opt) {
      if (opt.disabled || opt.hidden || !opt.value) return;

      var optionDiv = document.createElement('div');
      optionDiv.className = 'custom-dropdown__option';
      optionDiv.dataset.value = opt.value;

      var nameSpan = document.createElement('span');
      nameSpan.className = 'custom-dropdown__option-name';
      nameSpan.textContent = opt.textContent.trim();
      optionDiv.appendChild(nameSpan);

      optionDiv.addEventListener('click', function (e) {
        e.stopPropagation();

        select.value = this.dataset.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));

        value.innerHTML = '';
        var valName = document.createElement('span');
        valName.className = 'custom-dropdown__value-name';
        valName.textContent = nameSpan.textContent;
        value.appendChild(valName);

        dropdown.classList.add('has-value');
        dropdown.classList.remove('is-open', 'is-invalid');

        optionsPanel.querySelectorAll('.custom-dropdown__option').forEach(function (o) {
          o.classList.remove('is-selected');
        });
        this.classList.add('is-selected');
      });

      optionsPanel.appendChild(optionDiv);
    });

    dropdown.appendChild(trigger);
    dropdown.appendChild(optionsPanel);

    select.classList.add('custom-dropdown-hidden');
    field.appendChild(dropdown);

    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      document.querySelectorAll('.custom-dropdown.is-open').forEach(function (d) {
        if (d !== dropdown) d.classList.remove('is-open');
      });
      dropdown.classList.toggle('is-open');
    });
  }

  function initCustomDropdowns() {
    root.querySelectorAll('.config-field select').forEach(function (select) {
      buildSingleCustomDropdown(select);
    });
    document.addEventListener('click', function () {
      document.querySelectorAll('.custom-dropdown.is-open').forEach(function (d) {
        d.classList.remove('is-open');
      });
    });
  }

  /* ================================================================== */
  /* Fitting chart lightbox                                              */
  /* ================================================================== */

  function initChartLightbox() {
    var chartImg = root.querySelector('.junior-fit-chart img');
    if (!chartImg) return;

    chartImg.addEventListener('click', function () {
      var overlay = document.createElement('div');
      overlay.className = 'junior-fit-lightbox';

      var img = document.createElement('img');
      img.src = chartImg.src;
      img.alt = chartImg.alt;

      var close = document.createElement('button');
      close.type = 'button';
      close.className = 'junior-fit-lightbox__close';
      close.setAttribute('aria-label', 'Close');
      close.innerHTML = '&times;';

      overlay.appendChild(close);
      overlay.appendChild(img);
      document.body.appendChild(overlay);
      document.body.style.overflow = 'hidden';

      function dismiss() {
        overlay.remove();
        document.body.style.overflow = '';
        document.removeEventListener('keydown', onKey);
      }

      function onKey(e) {
        if (e.key === 'Escape') dismiss();
      }

      overlay.addEventListener('click', function (e) {
        if (e.target === overlay || e.target === close) dismiss();
      });
      document.addEventListener('keydown', onKey);
    });
  }

  /* ================================================================== */
  /* Price display                                                       */
  /* ================================================================== */

  /* Injects a separate price node rather than writing into the theme's
     Alpine-bound price element. Same approach and same element id as
     configurator.js, so the two can never both be visible. */
  function updateMainPrice(pence) {
    var customPrice = document.getElementById('configurator-custom-price');
    if (!customPrice) {
      var priceWrapper = document.querySelector('.product-info__price-wrapper');
      if (!priceWrapper) return;
      customPrice = document.createElement('div');
      customPrice.id = 'configurator-custom-price';
      customPrice.className = 'product-info__price-wrapper anm-fade-element mb-2 md:mb-2.5 is-visible';
      customPrice.innerHTML =
        '<div><div><p class="h3 price-wrapper h2">' +
        '<span class="main-price">' + CURRENCY + '0.00</span>' +
        '</p></div></div>';
      priceWrapper.parentNode.insertBefore(customPrice, priceWrapper.nextSibling);
    }
    customPrice.style.display = '';
    var priceEl = customPrice.querySelector('.main-price');
    if (priceEl) priceEl.textContent = CURRENCY + money(pence);
  }

  /* ================================================================== */
  /* Hide the theme's own buy UI                                         */
  /* ================================================================== */

  /* This product is custom_fit_only, so the theme's variant picker and
     add-to-basket must not be reachable. Mirrors the no-tabs branch of
     configurator.js init(). */
  function hideThemeBuyUI() {
    document.querySelectorAll(
      '.product-info__buy-buttons-wrapper, ' +
      '.product-info__variant-picker, ' +
      '.product-info__inventory-status-wrapper, ' +
      '.product-info__estimate-delivery-wrapper'
    ).forEach(function (el) {
      el.style.display = 'none';
      el.dataset.hiddenByConfigurator = 'true';
    });

    document.querySelectorAll('.product-info__text-with-icon_wrapper').forEach(function (el) {
      var text = (el.textContent || '').toLowerCase();
      if (text.indexOf('in stock') > -1 || text.indexOf('dispatched within') > -1) {
        el.style.display = 'none';
        el.dataset.hiddenByConfigurator = 'true';
      }
    });

    document.body.classList.add('configurator-custom-active');
  }

  /* Third-party apps (Salesfire, BOGOS, Klarna) inject after load and can
     re-add buy buttons. Re-run on late DOM changes, debounced. */
  function watchForLateInjection() {
    var timer = null;
    var observer = new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(hideThemeBuyUI, 150);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* ================================================================== */
  /* State refresh                                                       */
  /* ================================================================== */

  function refresh() {
    var row = currentHeightRow();

    setDerived('[data-junior-flex-wrap]', '[data-junior-flex-text]', row ? row.flex : null);
    setDerived('[data-junior-length-wrap]', '[data-junior-length-text]', row ? row.putter_length : null);

    var setOpt = selectedOption('[data-junior-set]');
    var price = setOpt ? parseInt(setOpt.dataset.setPrice || '0', 10) : 0;

    var setLabelEl = root.querySelector('[data-junior-set-label]');
    if (setLabelEl) {
      setLabelEl.textContent = setOpt ? CURRENCY + money(price) : 'Select a set';
    }

    var flexSummaryEl = root.querySelector('[data-junior-flex-summary]');
    if (flexSummaryEl) {
      flexSummaryEl.textContent = row ? row.flex : 'Select a height';
    }

    var totalEl = root.querySelector('[data-junior-total]');
    if (totalEl) totalEl.textContent = money(price);

    updateMainPrice(price);

    var addBtn = root.querySelector('[data-junior-add]');
    if (addBtn && addBtn.dataset.busy !== 'true') {
      addBtn.disabled = !setOpt;
      addBtn.textContent = setOpt
        ? 'Add to Basket \u2014 ' + CURRENCY + money(price)
        : 'Select a Set to Continue';
    }
  }

  /* ================================================================== */
  /* Add to cart                                                         */
  /* ================================================================== */

  function markInvalid(selector) {
    var el = root.querySelector(selector);
    if (!el) return;
    var field = el.closest('.config-field');
    if (!field) return;
    var dd = field.querySelector('.custom-dropdown');
    if (dd) dd.classList.add('is-invalid');
  }

  function clearInvalid() {
    root.querySelectorAll('.custom-dropdown.is-invalid').forEach(function (d) {
      d.classList.remove('is-invalid');
    });
  }

  function buildProperties(row) {
    var props = {
      'Product': root.dataset.productName || '',
      'Fit Type': 'Custom Fit'
    };

    var setOpt = selectedOption('[data-junior-set]');
    if (setOpt && setOpt.dataset.setLabel) props['Set'] = setOpt.dataset.setLabel;

    var dex = selectValue('[data-junior-dexterity]');
    if (dex) props['Dexterity'] = dex;

    var club = selectValue('[data-junior-club]');
    if (club) props['Club'] = club;

    var putterType = selectValue('[data-junior-putter-type]');
    if (putterType) props['Putter Head'] = putterType;

    var heightOpt = selectedOption('[data-junior-height]');
    if (heightOpt) props['Player Height'] = heightOpt.textContent.trim();

    var wrist = selectValue('[data-junior-wrist]');
    if (wrist) props['Wrist to Floor'] = wrist;

    if (row) {
      props['Flex'] = row.flex;
      if (root.querySelector('[data-junior-length-wrap]')) {
        props['Putter Length'] = row.putter_length;
      }
    }

    return props;
  }

  function addToCart() {
    showError('');
    clearInvalid();

    var setOpt = selectedOption('[data-junior-set]');
    var row = currentHeightRow();

    var missing = [];
    if (!setOpt) missing.push('[data-junior-set]');
    if (root.querySelector('[data-junior-dexterity]') && !selectValue('[data-junior-dexterity]')) {
      missing.push('[data-junior-dexterity]');
    }
    if (root.querySelector('[data-junior-club]') && !selectValue('[data-junior-club]')) {
      missing.push('[data-junior-club]');
    }
    if (root.querySelector('[data-junior-putter-type]') && !selectValue('[data-junior-putter-type]')) {
      missing.push('[data-junior-putter-type]');
    }
    if (!selectValue('[data-junior-height]')) missing.push('[data-junior-height]');
    if (root.querySelector('[data-junior-wrist]') && !selectValue('[data-junior-wrist]')) {
      missing.push('[data-junior-wrist]');
    }

    if (missing.length) {
      missing.forEach(markInvalid);
      showError('Please complete every option before adding to your basket.');
      return;
    }

    var addBtn = root.querySelector('[data-junior-add]');
    if (addBtn) {
      addBtn.dataset.busy = 'true';
      addBtn.disabled = true;
      addBtn.textContent = 'Adding...';
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: parseInt(setOpt.value, 10),
          quantity: 1,
          properties: buildProperties(row)
        }]
      })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (err) {
            throw new Error(err.description || err.message || 'Could not add to basket.');
          });
        }
        return response.json();
      })
      .then(function () {
        if (addBtn) addBtn.textContent = '\u2713 Added to Basket';
        sessionStorage.setItem('openCartDrawer', 'true');
        setTimeout(function () { window.location.reload(); }, 300);
      })
      .catch(function (error) {
        console.error('[junior-fit] add to cart failed', error);
        showError(error.message || 'Could not add to basket. Please try again.');
        if (addBtn) {
          addBtn.dataset.busy = '';
          addBtn.disabled = false;
        }
        refresh();
      });
  }

  /* ================================================================== */
  /* Init                                                                */
  /* ================================================================== */

  function init() {
    hideThemeBuyUI();
    watchForLateInjection();
    initCustomDropdowns();
    initChartLightbox();

    root.addEventListener('change', function () {
      showError('');
      refresh();
    });

    var addBtn = root.querySelector('[data-junior-add]');
    if (addBtn) {
      addBtn.addEventListener('click', function (e) {
        e.preventDefault();
        addToCart();
      });
    }

    /* Reopen the cart drawer after the post-add reload. */
    if (sessionStorage.getItem('openCartDrawer') === 'true') {
      sessionStorage.removeItem('openCartDrawer');
      setTimeout(function () {
        try {
          if (typeof Alpine !== 'undefined' && Alpine.store('xMiniCart')) {
            Alpine.store('xMiniCart').handleOpen();
          }
        } catch (e) {
          /* drawer unavailable: the item is still in the cart */
        }
      }, 500);
    }

    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
